import AdminPagination from '../AdminPagination';
import AdminState from '../AdminState';
import { ArrowRightIcon, ChevronDownIcon } from '../AdminIcon';

// Components/Tabs/AdminUserSearchTab.jsx

import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { AdminActions } from '../AdminLayout/AdminLayout';
import Backend from "../../../utils/api.js";
import AdminUserDetailsTab from "./AdminUserDetailsTab.jsx";
import { Input, Select, Button, Text, VStack, HStack, Badge } from '@chakra-ui/react';
import "./AdminUserSearchTab.css";

/**
 * AdminUserSearchTab Component
 * 
 * Allows administrators to search for users by UID, name, directoryID, or email.
 * Clicking on a user opens their details page.
 */
function AdminUserSearchTab({ user, labId, pushTab, revert }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const resultsContainerRef = useRef(null);

  const fetchPage = async (pageNum) => {
    if (!searchQuery.trim()) {
      setError("Please enter a search query");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await Backend.adminSearchUsers(
        user.UID,
        searchQuery.trim(),
        searchType,
        pageNum,
        20,
        labId
      );

      if (response.status === 200) {
        setHasSearched(true);
        setResults(response.items || []);
        setPage(response.page || pageNum);
        setTotalResults(response.total || 0);
        setTotalPages(response.pages || 0);
      } else if (response.status === 403) {
        setError("You do not have permission to perform this action");
      } else {
        setError("An error occurred while searching");
      }
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to connect to the server");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleUserClick = (targetUser) => {
    pushTab(
      <AdminUserDetailsTab adminRoute="user"
        key={`adminUserDetails-${targetUser.UID}`}
        user={user}
        targetUser={targetUser}
        labId={labId}
        pushTab={pushTab}
        revert={revert}
      />
    );
  };

  // Handle arrow key navigation
  const handleContainerKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const container = resultsContainerRef.current;
      if (!container) return;

      const buttons = Array.from(container.querySelectorAll("button"));
      if (buttons.length === 0) return;

      const currentIndex = buttons.findIndex(
        (btn) => btn === document.activeElement
      );

      let nextIndex;
      if (e.key === "ArrowUp") {
        nextIndex =
          currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
      } else {
        nextIndex =
          currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
      }

      e.preventDefault();
      buttons[nextIndex].focus();
    }
  };

  return (
    <Slider
      name="Find a user"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className="AdminUserSearchTab">
        {/* Search Controls */}
        <VStack spacing={4} align="stretch" className="search-controls">
          <HStack>
            <Input
              aria-label="Search users"
              placeholder="Search by UID, name, email, or directory ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              size="md"
              flex={1}
            />
          </HStack>
          <AdminActions>
            <Select icon={<ChevronDownIcon />}
              aria-label="Search field"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              size="md"
              width="200px"
            >
              <option value="all">All Fields</option>
              <option value="uid">UID</option>
              <option value="name">Name</option>
              <option value="directory_id">Directory ID</option>
              <option value="email">Email</option>
            </Select>
            <Button
              colorScheme="blue"
              size="md"
              onClick={handleSearch}
              isLoading={loading}
              aria-label={loading ? 'Searching…' : undefined}
            >
              Search
            </Button>
          </AdminActions>
        </VStack>

        {/* Error Display */}
        {error && (
          <AdminState status="error" title="Search could not be completed" description={error} onRetry={searchQuery.trim() ? handleSearch : undefined} mt={4} />
        )}

        {/* Loading State */}
        {loading && !hasSearched && (
          <AdminState status="loading" title="Searching users…" mt={4} />
        )}

        {/* Results */}
        {!error && hasSearched && (
          <div
            className={`search-results${loading ? ' admin-refreshing' : ''}`}
            aria-busy={loading}
            ref={resultsContainerRef}
            onKeyDown={handleContainerKeyDown}
          >
            {results.length === 0 ? (
              <AdminState title="No users found" description="Try a different name, email address, or UID, or change the search type." mt={4} />
            ) : (
              <VStack spacing={2} align="stretch">
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Found {totalResults} user(s)
                  {totalPages > 1 && ` — page ${page} of ${totalPages}`}
                </Text>
                {results.map((u) => (
                  <button
                    key={u.UID}
                    className="user-result-card"
                    onClick={() => handleUserClick(u)}
                    type="button"
                  >
                    <div className="user-result-info">
                      <div className="user-result-name">
                        {u.firstName} {u.lastName}
                        {u.isSuperAdmin && (
                          <Badge colorScheme="purple" ml={2}>
                            Super Admin
                          </Badge>
                        )}
                      </div>
                      <div className="user-result-details">
                        <span>UID: {u.UID}</span>
                        <span>•</span>
                        <span>{u.directoryID}</span>
                        <span>•</span>
                        <span>{u.email}</span>
                      </div>
                    </div>
                    <ArrowRightIcon className="user-result-arrow" />
                  </button>
                ))}
                <AdminPagination page={page} totalPages={totalPages} totalItems={totalResults} pageSize={20} isLoading={loading} onPageChange={fetchPage} itemLabel="users" />
              </VStack>
            )}
          </div>
        )}

        {/* Initial State */}
        {!loading && !error && !hasSearched && (
          <AdminState title="Find a user" description="Search by name, email address, or UID to view their details and space activity." mt={4} />
        )}
      </div>
    </Slider>
  );
}

AdminUserSearchTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default AdminUserSearchTab;
